import React, { useEffect, useState, useMemo } from "react";
import Head from "../../../layout/head/Head";
import Content from "../../../layout/content/Content";
import {
  Block,
  BlockHead,
  BlockHeadContent,
  BlockTitle,
  BlockBetween,
  BlockDes,
  Row,
  Col,
  Icon,
  Button,
} from "../../../components/Component";
import {
  Card,
  Badge,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Spinner,
  Nav,
  NavItem,
  NavLink,
} from "reactstrap";
import classnames from "classnames";
import { handleGetApis, handlePostApis } from "../../../service/Api";
import { useLocation, useNavigate } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

// Keep in sync with ryzhm_api/config/uploadLimits.js.
const MB = 1024 * 1024;
const MAX_BYTES = { image: 2 * MB, video: 15 * MB };
const ACCEPTED_TYPES = ["image/jpeg", "image/png", "image/webp", "image/avif", "video/mp4", "video/webm"];

const ProductMedia = () => {
  const navigate = useNavigate();
  const { search } = useLocation();
  const productId = new URLSearchParams(search).get("id");

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [product, setProduct] = useState(null);

  // Existing media from database
  const [mediaList, setMediaList] = useState([]);
  const [deletedIds, setDeletedIds] = useState([]);

  // Active Variant Tab Filter ("all" or variant id/size)
  const [activeTab, setActiveTab] = useState("all");

  // Upload Modal State
  const [uploadModal, setUploadModal] = useState(false);
  const [uploadVariant, setUploadVariant] = useState("");
  const [uploadFiles, setUploadFiles] = useState([]); // { file, media_type, is_primary, sort_order, previewUrl }

  // Preview Modal State
  const [previewModal, setPreviewModal] = useState({ open: false, item: null });

  // Delete Confirm Modal State
  const [deleteModal, setDeleteModal] = useState({ open: false, item: null, isNew: false, index: null });

  const hostUrl = process.env.REACT_APP_API_HOSTURL || "";

  // Helper for media URLs
  const formatMediaUrl = (url) => {
    if (!url) return "";
    if (url.startsWith("http://") || url.startsWith("https://") || url.startsWith("blob:")) {
      return url;
    }
    const cleanHost = hostUrl.endsWith("/") ? hostUrl.slice(0, -1) : hostUrl;
    const cleanPath = url.startsWith("/") ? url : `/${url}`;
    return `${cleanHost}${cleanPath}`;
  };

  /* ── Fetch Product Details & Media ────────────────────────── */
  const fetchProductDetails = async () => {
    if (!productId) {
      toast.error("Product ID missing");
      navigate("/product-list");
      return;
    }
    setLoading(true);
    try {
      const res = await handleGetApis(`admin/get/product/${productId}`);
      if (res?.data?.success) {
        const p = res.data.data;
        setProduct(p);

        // Map existing images/videos
        const existing = (p.images || []).map((img) => ({
          id: img.id,
          product_id: p.id,
          variant_id: img.variant_id || "",
          variant_size: img.variant_size || (p.variants?.find((v) => v.id === img.variant_id)?.size || ""),
          media_type: img.media_type || "image",
          media_url: img.media_url || img.image_url || "",
          is_primary: img.is_primary === 1 ? 1 : 0,
          sort_order: img.sort_order ?? 0,
        }));
        setMediaList(existing);
      } else {
        toast.error(res?.data?.message || "Failed to load product details");
      }
    } catch (err) {
      console.error(err);
      toast.error("Server error while fetching product");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProductDetails();
  }, [productId]);

  /* ── Variant Options ───────────────────────────────────────── */
  const variantOptions = useMemo(() => {
    if (!product?.variants) return [];
    return product.variants.map((v) => ({
      value: v.id || v.size,
      label: `${v.size}${v.sku ? ` (${v.sku})` : ""}${v.price ? ` - ₹${v.price}` : ""}`,
      size: v.size,
      id: v.id,
    }));
  }, [product]);

  /* ── Filtered Media List ───────────────────────────────────── */
  const filteredMedia = useMemo(() => {
    if (activeTab === "all") return mediaList;
    return mediaList.filter(
      (m) =>
        String(m.variant_id) === String(activeTab) ||
        String(m.variant_size) === String(activeTab)
    );
  }, [mediaList, activeTab]);

  /* ── Media Operations ──────────────────────────────────────── */
  const handleSetPrimary = (targetId) => {
    setMediaList((prev) =>
      prev.map((item) => ({
        ...item,
        is_primary: item.id === targetId ? 1 : 0,
      }))
    );
    toast.success("Set as primary media!");
  };

  const handleUpdateSortOrder = (id, newOrder) => {
    setMediaList((prev) =>
      prev.map((item) => (item.id === id ? { ...item, sort_order: Number(newOrder) } : item))
    );
  };

  const handleUpdateVariant = (id, newVariantId) => {
    const selectedVar = variantOptions.find((v) => String(v.value) === String(newVariantId));
    setMediaList((prev) =>
      prev.map((item) =>
        item.id === id
          ? {
              ...item,
              variant_id: newVariantId,
              variant_size: selectedVar?.size || "",
            }
          : item
      )
    );
  };

  const confirmDelete = (item) => {
    setDeleteModal({ open: true, item });
  };

  const executeDelete = () => {
    if (!deleteModal.item) return;
    const targetId = deleteModal.item.id;
    setDeletedIds((prev) => [...prev, targetId]);
    setMediaList((prev) => prev.filter((m) => m.id !== targetId));
    setDeleteModal({ open: false, item: null });
    toast.info("Media marked for deletion. Click 'Save Changes' to apply.");
  };

  /* ── Upload Modal Handlers ─────────────────────────────────── */
  const handleFilesSelect = (e) => {
    const picked = Array.from(e.target.files || []);
    // Same rules as the API (config/uploadLimits.js), checked here for instant feedback.
    const files = picked.filter((file) => {
      const kind = file.type.startsWith("video/") ? "video" : "image";
      if (!ACCEPTED_TYPES.includes(file.type)) {
        toast.error(`${file.name}: only JPG, PNG, WEBP, AVIF images or MP4, WEBM videos are allowed.`);
        return false;
      }
      if (file.size > MAX_BYTES[kind]) {
        toast.error(`${file.name}: ${kind} must be under ${MAX_BYTES[kind] / MB}MB.`);
        return false;
      }
      return true;
    });
    e.target.value = null;
    if (!files.length) return;

    const newItems = files.map((file, idx) => {
      const isVideo = file.type.startsWith("video/");
      return {
        file,
        media_type: isVideo ? "video" : "image",
        is_primary: mediaList.length === 0 && uploadFiles.length === 0 && idx === 0 ? 1 : 0,
        sort_order: mediaList.length + uploadFiles.length + idx,
        previewUrl: URL.createObjectURL(file),
      };
    });

    setUploadFiles((prev) => [...prev, ...newItems]);
  };

  const handleRemoveUploadFile = (index) => {
    setUploadFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleApplyUploadFiles = () => {
    if (!uploadFiles.length) {
      toast.warning("Please select at least one image or video.");
      return;
    }

    const selectedVar = variantOptions.find((v) => String(v.value) === String(uploadVariant));

    // Convert uploadFiles into temporary media items in mediaList
    const newItems = uploadFiles.map((uf) => ({
      id: "temp_" + Date.now() + "_" + Math.random(),
      file: uf.file,
      product_id: productId,
      variant_id: uploadVariant,
      variant_size: selectedVar?.size || "",
      media_type: uf.media_type,
      media_url: uf.previewUrl,
      is_primary: uf.is_primary,
      sort_order: uf.sort_order,
      is_new: true,
    }));

    setMediaList((prev) => [...prev, ...newItems]);
    setUploadFiles([]);
    setUploadModal(false);
    toast.success(`${newItems.length} media item(s) added to gallery! Click 'Save Changes' to upload.`);
  };

  /* ── Save All Changes to Server ────────────────────────────── */
  const handleSaveAllMedia = async () => {
    setSaving(true);
    const fd = new FormData();
    fd.append("product_id", productId);

    // 1️⃣ Deleted Media IDs
    deletedIds.forEach((id) => {
      if (!String(id).startsWith("temp_")) {
        fd.append("delete_ids", id);
      }
    });

    // 2️⃣ New media files with variant & order metadata
    const newItems = mediaList.filter((m) => m.is_new && m.file);
    newItems.forEach((m) => {
      fd.append("files", m.file);
      fd.append("variant_id", m.variant_id || "");
      fd.append("is_primary", m.is_primary ? 1 : 0);
      fd.append("media_type", m.media_type || "image");
      fd.append("sort_order", m.sort_order ?? 0);
    });

    // 3️⃣ Existing media metadata updates (variant_id, is_primary, sort_order)
    const existingItems = mediaList.filter((m) => !m.is_new);
    existingItems.forEach((m) => {
      fd.append(
        "update_meta",
        JSON.stringify({
          id: m.id,
          variant_id: m.variant_id ? m.variant_id : null,
          is_primary: m.is_primary ? 1 : 0,
          sort_order: Number(m.sort_order) || 0,
        })
      );
    });

    try {
      const res = await handlePostApis("admin/update/product/media", fd);
      if (res?.data?.success || res?.status === 200) {
        toast.success(res?.data?.message || "Product media successfully updated!");
        setDeletedIds([]);
        fetchProductDetails();
      } else {
        toast.error(res?.data?.message || "Failed to update media");
      }
    } catch (err) {
      console.error("Save media error:", err);
      toast.error("An error occurred while saving media");
    } finally {
      setSaving(false);
    }
  };

  return (
    <React.Fragment>
      <Head title={`Media Gallery - ${product?.name || "Product"}`} />
      <ToastContainer position="top-right" autoClose={3000} />

      <Content>
        {/* ── Page Header ────────────────────────────────────────── */}
        <BlockHead size="sm">
          <BlockBetween className="g-3 align-items-center flex-wrap">
            <BlockHeadContent>
              <div className="d-flex align-items-center gap-2">
                <Button
                  color="light"
                  outline
                  size="sm"
                  className="btn-icon btn-trigger"
                  onClick={() => navigate("/product-list")}
                >
                  <Icon name="arrow-left" />
                </Button>
                <div>
                  <BlockTitle page className="fs-20px mb-0">
                    Product Media
                  </BlockTitle>
                  <BlockDes className="text-soft">
                    {product ? (
                      <span>
                        Manage variant media for <strong className="text-dark">{product.name}</strong>
                        <span className="badge bg-light text-muted border ms-2">ID: #{product.id}</span>
                        {product.category_name && (
                          <span className="badge bg-outline-info ms-1">{product.category_name}</span>
                        )}
                      </span>
                    ) : (
                      "Manage images and videos"
                    )}
                  </BlockDes>
                </div>
              </div>
            </BlockHeadContent>

            <BlockHeadContent>
              <ul className="nk-block-tools g-2 flex-wrap">
                <li>
                  <Button
                    color="primary"
                    size="sm"
                    className="d-flex align-items-center"
                    onClick={() => {
                      setUploadVariant(activeTab === "all" ? "" : activeTab);
                      setUploadFiles([]);
                      setUploadModal(true);
                    }}
                  >
                    <Icon name="upload-cloud" />
                    <span className="ms-1">Upload Media</span>
                  </Button>
                </li>
                <li>
                  <Button
                    color="success"
                    size="sm"
                    className="d-flex align-items-center"
                    onClick={handleSaveAllMedia}
                    disabled={saving || loading}
                  >
                    {saving ? (
                      <>
                        <Spinner size="sm" className="me-1" />
                        <span>Saving...</span>
                      </>
                    ) : (
                      <>
                        <Icon name="check" />
                        <span className="ms-1">Save Changes</span>
                      </>
                    )}
                  </Button>
                </li>
              </ul>
            </BlockHeadContent>
          </BlockBetween>
        </BlockHead>

        {loading ? (
          <div className="text-center py-5">
            <Spinner color="primary" />
            <p className="text-soft mt-2 fs-14px">Loading media gallery...</p>
          </div>
        ) : (
          <Block>
            {/* ── Variant Filter Nav Tabs ───────────────────────────── */}
            <div className="card card-bordered mb-4">
              <div className="card-inner py-2 px-3">
                <Nav tabs className="card-nav card-nav-tabs border-0 flex-nowrap overflow-x-auto">
                  <NavItem>
                    <NavLink
                      tag="a"
                      href="#tab"
                      className={classnames({ active: activeTab === "all" })}
                      onClick={(e) => {
                        e.preventDefault();
                        setActiveTab("all");
                      }}
                      style={{ cursor: "pointer", whiteSpace: "nowrap" }}
                    >
                      <Icon name="grid-alt" className="me-1" />
                      All Media
                      <Badge color="light" className="text-dark ms-1">
                        {mediaList.length}
                      </Badge>
                    </NavLink>
                  </NavItem>
                  {product?.variants?.map((v) => {
                    const count = mediaList.filter(
                      (m) =>
                        String(m.variant_id) === String(v.id) ||
                        String(m.variant_size) === String(v.size)
                    ).length;
                    const isTabActive =
                      String(activeTab) === String(v.id) ||
                      String(activeTab) === String(v.size);
                    return (
                      <NavItem key={v.id || v.size}>
                        <NavLink
                          tag="a"
                          href="#tab"
                          className={classnames({ active: isTabActive })}
                          onClick={(e) => {
                            e.preventDefault();
                            setActiveTab(v.id || v.size);
                          }}
                          style={{ cursor: "pointer", whiteSpace: "nowrap" }}
                        >
                          <Icon name="package" className="me-1" />
                          {v.size}
                          <Badge color={isTabActive ? "primary" : "light"} className={isTabActive ? "ms-1" : "text-dark ms-1"}>
                            {count}
                          </Badge>
                        </NavLink>
                      </NavItem>
                    );
                  })}
                </Nav>
              </div>
            </div>

            {/* ── Media Gallery Grid ────────────────────────────────── */}
            {filteredMedia.length === 0 ? (
              <div className="card card-bordered text-center py-5 px-3">
                <div className="card-inner py-4">
                  <div className="nk-block-content text-center">
                    <div className="mb-3 text-primary opacity-50">
                      <Icon name="img" style={{ fontSize: 56 }} />
                    </div>
                    <h5 className="title mb-1">No Media Attached</h5>
                    <p className="text-soft fs-14px mb-4">
                      {activeTab === "all"
                        ? "This product currently has no images or videos uploaded."
                        : `No media has been assigned to variant "${activeTab}".`}
                    </p>
                    <Button
                      color="primary"
                      onClick={() => {
                        setUploadVariant(activeTab === "all" ? "" : activeTab);
                        setUploadFiles([]);
                        setUploadModal(true);
                      }}
                    >
                      <Icon name="upload-cloud" />
                      <span className="ms-1">Upload Media for this Variant</span>
                    </Button>
                  </div>
                </div>
              </div>
            ) : (
              <Row className="g-gs">
                {/* Add Media Quick Tile */}
                <Col xxl="3" lg="4" sm="6" xs="12">
                  <div
                    className="card card-bordered h-100 d-flex flex-column align-items-center justify-content-center text-center p-4 border-dashed cursor-pointer"
                    style={{
                      minHeight: 310,
                      backgroundColor: "#fafbfc",
                      borderRadius: 8,
                      transition: "all 0.2s",
                    }}
                    onClick={() => {
                      setUploadVariant(activeTab === "all" ? "" : activeTab);
                      setUploadFiles([]);
                      setUploadModal(true);
                    }}
                    onMouseEnter={(e) => (e.currentTarget.style.borderColor = "#6576ff")}
                    onMouseLeave={(e) => (e.currentTarget.style.borderColor = "#dbdfea")}
                  >
                    <div
                      className="rounded-circle d-flex align-items-center justify-content-center mb-2"
                      style={{ width: 54, height: 54, background: "#ebeef8", color: "#6576ff" }}
                    >
                      <Icon name="plus" className="fs-24px" />
                    </div>
                    <span className="fw-bold fs-15px text-dark">Add New Media</span>
                    <span className="text-soft fs-12px mt-1">Upload images / videos</span>
                  </div>
                </Col>

                {filteredMedia.map((m) => {
                  const isVid = m.media_type === "video";
                  const fullUrl = formatMediaUrl(m.media_url);

                  return (
                    <Col xxl="3" lg="4" sm="6" xs="12" key={m.id}>
                      <Card
                        className={classnames("card-bordered h-100 overflow-hidden shadow-none", {
                          "border-primary": m.is_primary === 1,
                        })}
                        style={{ borderRadius: 8 }}
                      >
                        {/* Media Visual Header */}
                        <div
                          style={{
                            height: 200,
                            background: "#0f172a",
                            position: "relative",
                            overflow: "hidden",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          {/* Top Badges */}
                          <div
                            className="position-absolute d-flex justify-content-between align-items-center w-100 px-2"
                            style={{ top: 8, zIndex: 3 }}
                          >
                            {m.is_primary === 1 ? (
                              <Badge color="primary" className="fw-bold shadow-sm" style={{ fontSize: 11 }}>
                                ⭐ PRIMARY
                              </Badge>
                            ) : (
                              <button
                                type="button"
                                className="btn btn-xs btn-light bg-white text-dark shadow-sm opacity-90 border-0"
                                style={{ fontSize: 11 }}
                                onClick={() => handleSetPrimary(m.id)}
                                title="Click to set as primary image"
                              >
                                Set Primary
                              </button>
                            )}

                            <Badge
                              color={isVid ? "danger" : "dark"}
                              className="fw-normal shadow-sm"
                              style={{ fontSize: 11 }}
                            >
                              {isVid ? "🎬 Video" : "🖼️ Image"}
                            </Badge>
                          </div>

                          {/* Media Preview Player/Image */}
                          <div
                            className="w-100 h-100 d-flex align-items-center justify-content-center cursor-pointer"
                            onClick={() => setPreviewModal({ open: true, item: m })}
                          >
                            {isVid ? (
                              <>
                                <video
                                  src={fullUrl}
                                  style={{ width: "100%", height: "100%", objectFit: "cover" }}
                                  muted
                                />
                                <div
                                  className="position-absolute rounded-circle bg-white text-dark d-flex align-items-center justify-content-center shadow"
                                  style={{ width: 44, height: 44, fontSize: 18 }}
                                >
                                  ▶
                                </div>
                              </>
                            ) : (
                              <img
                                src={fullUrl}
                                alt="Product"
                                style={{ width: "100%", height: "100%", objectFit: "contain", background: "#f8f9fc" }}
                                onError={(e) => {
                                  e.target.onerror = null;
                                  e.target.src = "https://via.placeholder.com/300x200?text=No+Image";
                                }}
                              />
                            )}
                          </div>

                          {m.is_new && (
                            <Badge
                              color="warning"
                              className="position-absolute"
                              style={{ bottom: 8, left: 8, zIndex: 3, fontSize: 10 }}
                            >
                              NEW (UNSAVED)
                            </Badge>
                          )}
                        </div>

                        {/* Card Controls Body */}
                        <div className="card-inner p-3">
                          {/* Variant Assignment */}
                          <div className="form-group mb-2">
                            <label className="form-label fs-11px text-soft text-uppercase mb-1">
                              Variant Link
                            </label>
                            <select
                              className="form-select form-select-sm"
                              value={m.variant_id || m.variant_size || ""}
                              onChange={(e) => handleUpdateVariant(m.id, e.target.value)}
                            >
                              <option value="">All Variants (General)</option>
                              {variantOptions.map((vo) => (
                                <option key={vo.value} value={vo.value}>
                                  {vo.label}
                                </option>
                              ))}
                            </select>
                          </div>

                          {/* Sort Order & Action Buttons */}
                          <div className="d-flex justify-content-between align-items-center pt-2 border-top mt-2">
                            <div className="d-flex align-items-center gap-1">
                              <span className="fs-12px text-soft">Order:</span>
                              <input
                                type="number"
                                className="form-control form-control-sm text-center"
                                style={{ width: 50, height: 28, padding: "2px 4px" }}
                                value={m.sort_order}
                                onChange={(e) => handleUpdateSortOrder(m.id, e.target.value)}
                              />
                            </div>

                            <div className="d-flex align-items-center gap-1">
                              <Button
                                color="light"
                                size="xs"
                                outline
                                className="btn-icon"
                                title="View Large"
                                onClick={() => setPreviewModal({ open: true, item: m })}
                              >
                                <Icon name="eye" />
                              </Button>
                              <Button
                                color="danger"
                                size="xs"
                                outline
                                className="btn-icon"
                                title="Delete"
                                onClick={() => confirmDelete(m)}
                              >
                                <Icon name="trash" />
                              </Button>
                            </div>
                          </div>
                        </div>
                      </Card>
                    </Col>
                  );
                })}
              </Row>
            )}
          </Block>
        )}
      </Content>

      {/* ══════════════════════════════════════════════════════════
          UPLOAD MEDIA MODAL
      ══════════════════════════════════════════════════════════ */}
      <Modal isOpen={uploadModal} toggle={() => setUploadModal(false)} size="lg" centered>
        <ModalHeader toggle={() => setUploadModal(false)}>
          Upload Media for {product?.name || "Product"}
        </ModalHeader>
        <ModalBody>
          <div className="form-group mb-3">
            <label className="form-label fw-bold">Select Target Variant</label>
            <select
              className="form-select"
              value={uploadVariant}
              onChange={(e) => setUploadVariant(e.target.value)}
            >
              <option value="">All Variants (General Product Media)</option>
              {variantOptions.map((vo) => (
                <option key={vo.value} value={vo.value}>
                  {vo.label}
                </option>
              ))}
            </select>
            <span className="form-note text-soft">
              Uploaded files will automatically be tagged to this variant.
            </span>
          </div>

          {/* Drag & Drop / File Selector Box */}
          <div
            className="border-dashed p-4 text-center rounded mb-3 bg-light cursor-pointer"
            style={{ borderColor: "#6576ff", background: "#f8f9fc" }}
            onClick={() => document.getElementById("mediaFileInput").click()}
          >
            <input
              id="mediaFileInput"
              type="file"
              multiple
              accept={ACCEPTED_TYPES.join(",")}
              className="d-none"
              onChange={handleFilesSelect}
            />
            <div className="text-primary mb-2">
              <Icon name="upload-cloud" style={{ fontSize: 42 }} />
            </div>
            <h6 className="title fs-15px mb-1">Click to browse or drop images/videos</h6>
            <p className="text-soft fs-12px mb-0">
              Supports JPG, PNG, WEBP, and MP4 files. Select multiple files at once.
            </p>
          </div>

          {/* Selected Files Preview List */}
          {uploadFiles.length > 0 && (
            <div>
              <label className="form-label fw-bold mb-2">
                Selected Files ({uploadFiles.length})
              </label>
              <div className="d-flex flex-column gap-2" style={{ maxHeight: 250, overflowY: "auto", height:"auto" }}>
                {uploadFiles.map((uf, idx) => (
                  <div
                    key={idx}
                    className="d-flex align-items-center justify-content-between p-2 border rounded bg-white"
                  >
                    <div className="d-flex align-items-center gap-2">
                      <div
                        style={{
                          width: 48,
                          height: 48,
                          borderRadius: 6,
                          background: "#0f172a",
                          overflow: "hidden",
                        }}
                      >
                        {uf.media_type === "video" ? (
                          <video src={uf.previewUrl} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                        ) : (
                          <img src={uf.previewUrl} alt="prev" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                        )}
                      </div>
                      <div>
                        <span className="fw-bold fs-13px d-block text-truncate" style={{ maxWidth: 220 }}>
                          {uf.file.name}
                        </span>
                        <span className="text-soft fs-11px">
                          {(uf.file.size / 1024).toFixed(0)} KB &bull;{" "}
                          <span className="text-capitalize">{uf.media_type}</span>
                        </span>
                      </div>
                    </div>

                    <div className="d-flex align-items-center gap-2">
                      <Button
                        color="danger"
                        size="xs"
                        outline
                        className="btn-icon"
                        onClick={() => handleRemoveUploadFile(idx)}
                      >
                        <Icon name="trash" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </ModalBody>
        <ModalFooter className="bg-light">
          <Button color="primary" onClick={handleApplyUploadFiles} disabled={!uploadFiles.length}>
            <Icon name="check" /> <span className="ms-1">Add to Gallery</span>
          </Button>
          <Button color="light" onClick={() => setUploadModal(false)}>
            Cancel
          </Button>
        </ModalFooter>
      </Modal>

      {/* ══════════════════════════════════════════════════════════
          MEDIA LARGE PREVIEW MODAL
      ══════════════════════════════════════════════════════════ */}
      <Modal
        isOpen={previewModal.open}
        toggle={() => setPreviewModal({ open: false, item: null })}
        size="lg"
        centered
      >
        <ModalHeader toggle={() => setPreviewModal({ open: false, item: null })}>
          {previewModal.item?.media_type === "video" ? "🎬 Video Player" : "🖼️ Image Preview"}
        </ModalHeader>
        <ModalBody className="p-0 text-center bg-dark d-flex align-items-center justify-content-center" style={{ minHeight: 320 }}>
          {previewModal.item && (
            previewModal.item.media_type === "video" ? (
              <video
                src={formatMediaUrl(previewModal.item.media_url)}
                controls
                autoPlay
                style={{ width: "100%", maxHeight: "75vh", objectFit: "contain" }}
              />
            ) : (
              <img
                src={formatMediaUrl(previewModal.item.media_url)}
                alt="Full Preview"
                style={{ maxWidth: "100%", maxHeight: "75vh", objectFit: "contain" }}
              />
            )
          )}
        </ModalBody>
      </Modal>

      {/* ══════════════════════════════════════════════════════════
          DELETE CONFIRMATION MODAL
      ══════════════════════════════════════════════════════════ */}
      <Modal
        isOpen={deleteModal.open}
        toggle={() => setDeleteModal({ open: false, item: null })}
        size="sm"
        centered
      >
        <ModalHeader toggle={() => setDeleteModal({ open: false, item: null })}>
          Confirm Deletion
        </ModalHeader>
        <ModalBody>
          Are you sure you want to remove this media item? It will be removed permanently after saving changes.
        </ModalBody>
        <ModalFooter>
          <Button color="danger" size="sm" onClick={executeDelete}>
            Yes, Remove
          </Button>
          <Button color="light" size="sm" onClick={() => setDeleteModal({ open: false, item: null })}>
            Cancel
          </Button>
        </ModalFooter>
      </Modal>
    </React.Fragment>
  );
};

export default ProductMedia;
